import React, { useState, useEffect, useCallback } from 'react';
import {
   Container,
   Box,
   Typography,
   Paper,
   Button,
   IconButton,
   Chip,
   Dialog,
   DialogTitle,
   DialogContent,
   DialogActions,
   FormControl,
   FormGroup,
   FormControlLabel,
   Checkbox,
   TextField,
   MenuItem,
   Select,
   Tooltip,
   Divider,
   Grid,
   Card,
   CardContent,
   CircularProgress,
   Alert,
   SelectChangeEvent,
   InputLabel,
   useMediaQuery,
   useTheme,
   Snackbar,
} from '@mui/material';
import {
   useResponsive,
   useResponsiveTypography,
   useResponsiveSpacing,
} from '../../utils/responsive';
import {
   CalendarMonth as CalendarIcon,
   Event as EventIcon,
   Flight as FlightIcon,
   FilterList as FilterIcon,
   Today as TodayIcon,
   ViewDay as DayViewIcon,
   ViewWeek as WeekViewIcon,
   ViewModule as MonthViewIcon,
   ViewComfy as YearViewIcon,
   ChevronLeft as ChevronLeftIcon,
   ChevronRight as ChevronRightIcon,
   Person as PersonIcon,
   People as PeopleIcon,
   LocationOn as LocationIcon,
   AccessTime as TimeIcon,
   Close as CloseIcon,
} from '@mui/icons-material';
import * as ReactBigCalendar from 'react-big-calendar';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import './styles.css'; // Custom styles for the calendar
import { dateFnsLocalizer } from 'react-big-calendar';
import format from 'date-fns/format';
import parse from 'date-fns/parse';
import startOfWeek from 'date-fns/startOfWeek';
import getDay from 'date-fns/getDay';
import enUS from 'date-fns/locale/en-US';
import {
   collection,
   query,
   where,
   orderBy,
   getDocs,
   Timestamp,
   addDoc,
   updateDoc,
   serverTimestamp,
   doc,
} from 'firebase/firestore';
import { db } from '../../firebase/config';
import { useUser } from '../../contexts/UserContext';
import { useNavigate } from 'react-router-dom';
import TripEventModal, { TripEventModalData } from '../../components/modals/TripEventModal';
import {
   addMonths,
   isSameMonth,
   setMonth,
   getMonth,
   getYear,
   setYear,
   addYears,
   isSameYear,
   startOfMonth,
   endOfMonth,
} from 'date-fns';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { DateTimePicker, LocalizationProvider, DateTimePickerProps } from '@mui/x-date-pickers';
import { get_all_events_and_shared_events, get_all_trips_and_shared_trips } from '../../services';
import { useSubscription } from '../../contexts/SubscriptionContext';
import GooglePlacesSearch from '../../components/common/GooglePlacesSearch';

// Safe format function to handle invalid dates
const safeFormat = (date: any, formatStr: string): string => {
   try {
      if (!date) return '';
      const dateObj = date instanceof Date ? date : new Date(date);
      if (isNaN(dateObj.getTime())) return '';
      return format(dateObj, formatStr);
   } catch (error) {
      console.warn('Date formatting error:', error);
      return '';
   }
};

// Calendar localization setup
const locales = {
   'en-US': enUS,
};

const localizer = ReactBigCalendar.dateFnsLocalizer({
   format,
   parse,
   startOfWeek,
   getDay,
   locales,
});

// Types
interface CalendarEvent extends ReactBigCalendar.Event {
   id: string;
   title: string;
   start: Date;
   end: Date;
   allDay?: boolean;
   type: 'trip' | 'event';
   isOwner: boolean;
   resource?: any; // Additional data like location, description, etc.
}

interface Trip {
   id: string;
   title: string;
   startDate: Date;
   endDate: Date;
   primaryDestination?: {
      name: string;
      coordinates: [number, number];
   };
   owner: string;
   coverPhotoUrl?: string;
   description?: string;
}

interface Event {
   id: string;
   title: string;
   startDate: Date;
   endDate?: Date;
   location?: {
      name: string;
      address: string;
      coordinates: [number, number];
   };
   userId: string;
   coverPhotoUrl?: string;
   description?: string;
   eventType: string;
   isRecurring?: boolean;
   recurrencePattern?: string;
   recurrenceInterval?: number;
   recurrenceEndType?: 'occurrences' | 'date' | 'never';
   recurrenceEndOccurrences?: number;
   recurrenceEndDate?: Date | null;
}

interface FilterOptions {
   showTrips: boolean;
   showEvents: boolean;
   showOwned: boolean;
   showInvited: boolean;
}

// Define ToolbarProps interface
interface ToolbarProps {
   label: string;
   view: string;
   views: string[];
   onNavigate: (action: 'PREV' | 'NEXT' | 'TODAY') => void;
   onView: (view: string) => void;
}

const CalendarPage: React.FC = () => {
   const { currentUser } = useUser();
   const navigate = useNavigate();

   // Add responsive hooks
   const theme = useTheme();
   const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
   const isTablet = useMediaQuery(theme.breakpoints.between('sm', 'md'));
   const { isMobile: responsiveIsMobile, deviceType } = useResponsive();
   const { fontSize, getFontSize } = useResponsiveTypography();
   const { spacing, getSpacing } = useResponsiveSpacing();
   const [calendarView, setCalendarView] = useState<ReactBigCalendar.View | 'year'>('month');
   const [currentDate, setCurrentDate] = useState(new Date());
   const [calendarEvents, setCalendarEvents] = useState<CalendarEvent[]>([]);
   const [loading, setLoading] = useState(true);
   const [error, setError] = useState<string | null>(null);
   const [selectedEvent, setSelectedEvent] = useState<CalendarEvent | null>(null);
   const [detailsOpen, setDetailsOpen] = useState(false);
   const [modalData, setModalData] = useState<TripEventModalData | null>(null);
   const [modalOpen, setModalOpen] = useState(false);
   const [filterOptions, setFilterOptions] = useState<FilterOptions>({
      showTrips: true,
      showEvents: true,
      showOwned: true,
      showInvited: true,
   });
   const [filterDialogOpen, setFilterDialogOpen] = useState(false);
   const [yearRange, setYearRange] = useState<{ start: number; end: number }>({
      start: getYear(new Date()) - 1,
      end: getYear(new Date()) + 3,
   });
   const [addEventDialogOpen, setAddEventDialogOpen] = useState(false);
   const [addTripDialogOpen, setAddTripDialogOpen] = useState(false);
   const [calendarSyncDialogOpen, setCalendarSyncDialogOpen] = useState(false);

   // Toast notification state
   const [toastOpen, setToastOpen] = useState(false);
   const [toastMessage, setToastMessage] = useState('');
   const [toastSeverity, setToastSeverity] = useState<'success' | 'error'>('success');
   const [newEvent, setNewEvent] = useState<{
      title: string;
      startDate: Date;
      endDate: Date;
      location?: { name: string; address: string; coordinates: [number, number] };
      description?: string;
      eventType: string;
   }>({
      title: '',
      startDate: new Date(),
      endDate: new Date(new Date().setHours(new Date().getHours() + 2)),
      location: undefined,
      description: '',
      eventType: 'personal',
   });
   const [newTrip, setNewTrip] = useState<{
      title: string;
      startDate: Date;
      endDate: Date;
      primaryDestination?: { address: string; name: string; coordinates: [number, number] };
      description?: string;
   }>({
      title: '',
      startDate: new Date(),
      endDate: new Date(new Date().setDate(new Date().getDate() + 7)),
      primaryDestination: undefined,
      description: '',
   });
   const [savingEvent, setSavingEvent] = useState(false);
   const [savingTrip, setSavingTrip] = useState(false);
   const [eventSaveError, setEventSaveError] = useState<string | null>(null);
   const [tripSaveError, setTripSaveError] = useState<string | null>(null);
   const [syncItems, setSyncItems] = useState<string[]>([]);
   const [syncSettings, setSyncSettings] = useState({ autoSync: false });
   const [syncingCalendar, setSyncingCalendar] = useState(false);
   const { checkSubscriptionStatus } = useSubscription();

   // Fetch trips and events
   useEffect(() => {
      if (!currentUser) return;

      const fetchData = async () => {
         setLoading(true);
         setError(null);

         try {
            const calendarEvents: any[] = [];
            const trips = (await get_all_trips_and_shared_trips(
               currentUser.uid,
               currentUser.email || ''
            )) as any[];
            const events = (await get_all_events_and_shared_events(
               currentUser.uid,
               currentUser.email || ''
            )) as any[];

            for (const trip of trips) {
               // Safely parse dates from backend API
               let startDate: Date;
               let endDate: Date;

               try {
                  if (trip.startDate instanceof Timestamp) {
                     startDate = trip.startDate.toDate();
                  } else if (trip.startDate) {
                     startDate = new Date(trip.startDate);
                  } else {
                     startDate = new Date(); // fallback to current date
                  }

                  if (trip.endDate instanceof Timestamp) {
                     endDate = trip.endDate.toDate();
                  } else if (trip.endDate) {
                     endDate = new Date(trip.endDate);
                  } else {
                     endDate = new Date(); // fallback to current date
                  }

                  // Validate dates
                  if (isNaN(startDate.getTime())) {
                     startDate = new Date();
                  }
                  if (isNaN(endDate.getTime())) {
                     endDate = new Date();
                  }
               } catch (error) {
                  console.warn('Invalid date in trip:', trip.id, error);
                  startDate = new Date();
                  endDate = new Date();
               }

               calendarEvents.push({
                  id: trip.id,
                  title: trip.title,
                  start: startDate,
                  end: endDate,
                  allDay: true,
                  type: 'trip',
                  isOwner: trip.owner === currentUser.uid || trip.userId === currentUser.uid,
                  resource: {
                     ...trip,
                     owner: trip.owner || trip.userId,
                  },
               });
            }

            for (const event of events) {
               // Safely parse dates from backend API
               let startDate: Date;
               let endDate: Date;

               try {
                  if (event.startDate instanceof Timestamp) {
                     startDate = event.startDate.toDate();
                  } else if (event.startDate) {
                     startDate = new Date(event.startDate);
                  } else {
                     startDate = new Date(); // fallback to current date
                  }

                  if (event.endDate instanceof Timestamp) {
                     endDate = event.endDate.toDate();
                  } else if (event.endDate) {
                     endDate = new Date(event.endDate);
                  } else {
                     // If no end date, default to 2 hours after start
                     endDate = new Date(startDate.getTime() + 2 * 60 * 60 * 1000);
                  }

                  // Validate dates
                  if (isNaN(startDate.getTime())) {
                     startDate = new Date();
                  }
                  if (isNaN(endDate.getTime())) {
                     endDate = new Date(startDate.getTime() + 2 * 60 * 60 * 1000);
                  }
               } catch (error) {
                  console.warn('Invalid date in event:', event.id, error);
                  startDate = new Date();
                  endDate = new Date(startDate.getTime() + 2 * 60 * 60 * 1000);
               }

               const isOwner =
                  event.userId === currentUser.uid || event.owner === currentUser.uid;
               if (event.isRecurring && event.recurrencePattern) {
                  const expanded = expandRecurringEvent(event.id, event, isOwner);
                  calendarEvents.push(...expanded);
               } else {
                  calendarEvents.push({
                     id: event.id,
                     title: event.title,
                     start: startDate,
                     end: endDate,
                     allDay: false,
                     type: 'event',
                     isOwner,
                     resource: {
                        ...event,
                        userId: event.userId || event.owner,
                     },
                  });
               }
            }
            setCalendarEvents(calendarEvents);
         } catch (err) {
            console.error('Error fetching calendar data:', err);
            setError('Failed to load calendar data. Please try again.');
         } finally {
            setLoading(false);
         }
      };

      fetchData();
   }, [currentUser]);

   // Check for Google Calendar sync result from URL parameters
   useEffect(() => {
      const urlParams = new URLSearchParams(window.location.search);
      const googleAuth = urlParams.get('google_auth');
      const syncResult = urlParams.get('sync_result');
      const reason = urlParams.get('reason');

      if (googleAuth === 'success') {
         if (syncResult === 'success') {
            setToastMessage('🎉 Successfully synced to Google Calendar!');
            setToastSeverity('success');
            setToastOpen(true);
         } else if (syncResult === 'sync_failed') {
            setToastMessage('❌ Event was not synced to Google Calendar. Please try again.');
            setToastSeverity('error');
            setToastOpen(true);
         } else {
            setToastMessage('✅ Google authentication successful!');
            setToastSeverity('success');
            setToastOpen(true);
         }
      } else if (googleAuth === 'error') {
         let errorMessage = '❌ Google authentication failed.';
         if (reason === 'token_exchange_failed') {
            errorMessage = '❌ Failed to exchange authorization code. Please try again.';
         } else if (reason === 'missing_params') {
            errorMessage = '❌ Missing authentication parameters. Please try again.';
         } else if (reason) {
            errorMessage = `❌ Authentication error: ${reason}`;
         }
         setToastMessage(errorMessage);
         setToastSeverity('error');
         setToastOpen(true);
      }

      // Clear URL parameters after processing
      if (googleAuth) {
         const newUrl = window.location.pathname;
         window.history.replaceState({}, document.title, newUrl);
      }
   }, []);

   // Fix for missing date numbers - add them programmatically after calendar renders
   useEffect(() => {
      const addDateNumbers = () => {
         setTimeout(() => {
            // First, remove any existing custom date cells to prevent duplicates
            document.querySelectorAll('.custom-date-cell').forEach((cell) => cell.remove());

            const dayBgElements = document.querySelectorAll('.rbc-month-view .rbc-day-bg');
            dayBgElements.forEach((dayBg, index) => {
               if (calendarView === 'month') {
                  // Calculate the date for this cell
                  const firstDayOfMonth = new Date(
                     currentDate.getFullYear(),
                     currentDate.getMonth(),
                     1
                  );
                  const startOfCalendar = new Date(firstDayOfMonth);
                  startOfCalendar.setDate(startOfCalendar.getDate() - startOfCalendar.getDay());

                  const cellDate = new Date(startOfCalendar);
                  cellDate.setDate(cellDate.getDate() + index);

                  // Create date cell element
                  const dateCell = document.createElement('div');
                  dateCell.className = 'custom-date-cell';
                  dateCell.innerHTML = `<a href="#" onclick="return false;">${cellDate.getDate()}</a>`;

                  // Style the date cell
                  Object.assign(dateCell.style, {
                     position: 'absolute',
                     top: '2px',
                     right: '4px',
                     zIndex: '15',
                     fontSize: '12px',
                     fontWeight: '600',
                     color: '#333',
                     background: 'rgba(255, 255, 255, 0.9)',
                     padding: '2px 4px',
                     borderRadius: '3px',
                     minWidth: '18px',
                     textAlign: 'center',
                     lineHeight: '1',
                     border: '1px solid rgba(0, 0, 0, 0.1)',
                     display: 'block',
                     visibility: 'visible',
                  });

                  // Add to day background
                  dayBg.appendChild(dateCell);
               }
            });
         }, 100);
      };

      if (calendarView === 'month') {
         addDateNumbers();
      }
   }, [currentDate, calendarView, calendarEvents]);

   // Function to expand recurring events into multiple instances
   const expandRecurringEvent = (
      eventId: string,
      event: any,
      isOwner: boolean
   ): CalendarEvent[] => {
      const expandedEvents: CalendarEvent[] = [];

      // Make sure we have valid data
      if (!event.startDate || !event.isRecurring || !event.recurrencePattern) {
         // If basic recurrence data is missing, return the event as a non-recurring one
         expandedEvents.push({
            id: eventId,
            title: event.title,
            start:
               event.startDate instanceof Timestamp
                  ? event.startDate.toDate()
                  : new Date(event.startDate),
            end:
               event.endDate instanceof Timestamp
                  ? event.endDate.toDate()
                  : event.endDate
                    ? new Date(event.endDate)
                    : event.startDate instanceof Timestamp
                      ? new Date(event.startDate.toDate().getTime() + 2 * 60 * 60 * 1000)
                      : new Date(new Date(event.startDate).getTime() + 2 * 60 * 60 * 1000),
            allDay: false,
            type: 'event',
            isOwner: isOwner,
            resource: {
               ...event,
               userId: event.userId,
            },
         });
         return expandedEvents;
      }

      // Convert to Date objects if they are Timestamps
      const startDate =
         event.startDate instanceof Timestamp
            ? event.startDate.toDate()
            : new Date(event.startDate);
      const endDate =
         event.endDate instanceof Timestamp
            ? event.endDate.toDate()
            : event.endDate
              ? new Date(event.endDate)
              : new Date(startDate.getTime() + 2 * 60 * 60 * 1000);

      // Calculate event duration in milliseconds
      const eventDuration = endDate.getTime() - startDate.getTime();

      // Determine the end date of the recurrence
      let recurrenceEndDate: Date;
      if (event.recurrenceEndType === 'date' && event.recurrenceEndDate) {
         recurrenceEndDate =
            event.recurrenceEndDate instanceof Timestamp
               ? event.recurrenceEndDate.toDate()
               : new Date(event.recurrenceEndDate);
      } else if (event.recurrenceEndType === 'occurrences' && event.recurrenceEndOccurrences) {
         // Calculate an approximate end date based on occurrences and pattern
         recurrenceEndDate = calculateEndDateFromOccurrences(
            startDate,
            event.recurrencePattern,
            event.recurrenceInterval || 1,
            event.recurrenceEndOccurrences
         );
      } else {
         // If no end is specified or "never", set a reasonable limit (e.g., 1 year)
         recurrenceEndDate = new Date(startDate);
         recurrenceEndDate.setFullYear(recurrenceEndDate.getFullYear() + 1);
      }

      // Generate recurring event instances
      let currentDate = new Date(startDate);
      let occurrenceCount = 0;
      const maxOccurrences =
         event.recurrenceEndType === 'occurrences' ? event.recurrenceEndOccurrences : 100; // Limit to prevent infinite loops

      while (currentDate <= recurrenceEndDate && occurrenceCount < maxOccurrences) {
         // Create a new instance of the event
         const instanceEndDate = new Date(currentDate.getTime() + eventDuration);

         expandedEvents.push({
            id: `${eventId}_${occurrenceCount}`,
            title: event.title,
            start: new Date(currentDate),
            end: instanceEndDate,
            allDay: false,
            type: 'event',
            isOwner: isOwner,
            resource: {
               ...event,
               userId: event.userId,
               isRecurrenceInstance: true,
               originalEventId: eventId,
               occurrenceIndex: occurrenceCount,
            },
         });

         // Move to the next occurrence
         currentDate = getNextOccurrence(
            currentDate,
            event.recurrencePattern,
            event.recurrenceInterval || 1
         );
         occurrenceCount++;
      }

      return expandedEvents;
   };

   // Helper function to calculate next occurrence based on recurrence pattern
   const getNextOccurrence = (date: Date, pattern: string, interval: number): Date => {
      const next = new Date(date);

      switch (pattern) {
         case 'daily':
            next.setDate(next.getDate() + interval);
            break;
         case 'weekly':
            next.setDate(next.getDate() + 7 * interval);
            break;
         case 'monthly':
            next.setMonth(next.getMonth() + interval);
            break;
         case 'yearly':
            next.setFullYear(next.getFullYear() + interval);
            break;
      }

      return next;
   };

   // Helper function to calculate end date from number of occurrences
   const calculateEndDateFromOccurrences = (
      startDate: Date,
      pattern: string,
      interval: number,
      occurrences: number
   ): Date => {
      const endDate = new Date(startDate);

      switch (pattern) {
         case 'daily':
            endDate.setDate(endDate.getDate() + interval * occurrences);
            break;
         case 'weekly':
            endDate.setDate(endDate.getDate() + 7 * interval * occurrences);
            break;
         case 'monthly':
            endDate.setMonth(endDate.getMonth() + interval * occurrences);
            break;
         case 'yearly':
            endDate.setFullYear(endDate.getFullYear() + interval * occurrences);
            break;
      }

      return endDate;
   };

   // Filter the events based on filter options
   const filteredEvents = calendarEvents.filter((event) => {
      // Hide all trips per user request
      if (event.type === 'trip') return false;
      if (event.type === 'event' && !filterOptions.showEvents) return false;
      if (event.isOwner && !filterOptions.showOwned) return false;
      if (!event.isOwner && !filterOptions.showInvited) return false;
      return true;
   });

   // Handle calendar navigation
   const handleNavigate = (newDate: Date) => {
      setCurrentDate(newDate);
   };

   // Handle view change
   const handleViewChange = (view: ReactBigCalendar.View | 'year') => {
      setCalendarView(view);
   };

   // Handle event selection
   const handleSelectEvent = (event: CalendarEvent) => {
      // Prepare modal data
      const modalData: TripEventModalData = {
         id: event.id,
         type: event.type,
         title: event.title,
         description: event.resource?.description,
         startDate: event.start,
         endDate: event.end,
         location:
            event.type === 'trip'
               ? event.resource?.primaryDestination?.name
               : event.resource?.location?.name,
         destination: event.type === 'trip' ? event.resource?.primaryDestination?.name : undefined,
         coverImageUrl: event.resource?.coverPhotoUrl || event.resource?.coverPhoto,
         createdByName: event.resource?.createdByName,
         participantCount: event.resource?.participantCount,
         photoCount: event.resource?.photoCount,
      };

      setModalData(modalData);
      setModalOpen(true);
   };

   // Handle filter changes
   const handleFilterChange = (event: React.ChangeEvent<HTMLInputElement>) => {
      const { name, checked } = event.target;
      setFilterOptions((prev) => ({
         ...prev,
         [name]: checked,
      }));
   };

   // Create a new event
   const createEvent = async () => {
      if (!currentUser) return;
      if (!newEvent.title.trim()) {
         setEventSaveError('Event title is required');
         return;
      }

      setSavingEvent(true);
      setEventSaveError(null);

      try {
         // Create the event document in Firestore
         const eventData = {
            title: newEvent.title.trim(),
            startDate: Timestamp.fromDate(newEvent.startDate),
            endDate: Timestamp.fromDate(newEvent.endDate),
            location: newEvent.location,
            description: newEvent.description?.trim(),
            eventType: newEvent.eventType,
            createdAt: serverTimestamp(),
            updatedAt: serverTimestamp(),
            isPublic: false,
            owner: currentUser?.uid,
            userId: currentUser.uid,
            isRecurring: false,
            photoCount: 0,
            participants: [
               {
                  userId: currentUser.uid,
                  email: currentUser.email || '',
                  displayName: currentUser.displayName || 'Event Owner',
                  photoURL: currentUser.photoURL || '',
                  role: 'owner',
                  status: 'accepted',
                  invitedAt: new Date().toISOString(),
                  invitedBy: currentUser.uid,
                  acceptedAt: new Date().toISOString(),
               },
            ],
         };

         // Use backend API to create event with proper members array
         const { createApiUrl } = await import('../../utils/apiConfig');
         const { getAuthHeaders } = await import('../../utils/auth');
         const apiUrl = createApiUrl('/api/events');
         const headers = await getAuthHeaders();

         const response = await fetch(apiUrl, {
            method: 'POST',
            headers,
            body: JSON.stringify({
               title: newEvent.title.trim(),
               description: newEvent.description?.trim(),
               startDate: newEvent.startDate.toISOString(),
               endDate: newEvent.endDate.toISOString(),
               location: newEvent.location,
               eventType: newEvent.eventType,
               isPublic: false,
               userId: currentUser.uid,
            }),
         });

         const result = await response.json();

         if (!response.ok) {
            throw new Error(result.error || 'Failed to create event');
         }

         const docRef = { id: result.eventId };

         // Add the new event to the calendar events
         const newCalendarEvent: CalendarEvent = {
            id: docRef.id,
            title: newEvent.title,
            start: newEvent.startDate,
            end: newEvent.endDate,
            allDay: false,
            type: 'event',
            isOwner: true,
            resource: {
               ...eventData,
               id: docRef.id,
            },
         };

         setCalendarEvents((prev) => [...prev, newCalendarEvent]);

         // Reset form and close dialog
         setNewEvent({
            title: '',
            startDate: new Date(),
            endDate: new Date(new Date().setHours(new Date().getHours() + 2)),
            location: undefined,
            description: '',
            eventType: 'personal',
         });
         setAddEventDialogOpen(false);
      } catch (error) {
         console.error('Error creating event:', error);
         setEventSaveError('Failed to create event. Please try again.');
      } finally {
         setSavingEvent(false);
      }
   };

   // Create a new trip
   const createTrip = async () => {
      if (!currentUser) return;
      if (!newTrip.title.trim()) {
         setTripSaveError('Trip title is required');
         return;
      }

      setSavingTrip(true);
      setTripSaveError(null);

      try {
         // Create the trip document in Firestore
         const tripData = {
            title: newTrip.title,
            description: newTrip.description,
            primaryDestination: {
               address: newTrip.primaryDestination?.address,
               name: newTrip.primaryDestination?.name,
               coordinates: newTrip.primaryDestination?.coordinates,
            },
            startDate: Timestamp.fromDate(newTrip.startDate),
            endDate: Timestamp.fromDate(newTrip.endDate),
            createdAt: serverTimestamp(),
            updatedAt: serverTimestamp(),
            owner: currentUser.uid,
            isHistorical: true,
         };

         // Use backend API to create trip with proper members array
         const { createApiUrl } = await import('../../utils/apiConfig');
         const { getAuthHeaders } = await import('../../utils/auth');
         const headers = await getAuthHeaders();
         const apiUrl = createApiUrl('/api/trips');

         const response = await fetch(apiUrl, {
            method: 'POST',
            headers,
            body: JSON.stringify({
               title: newTrip.title,
               description: newTrip.description,
               startDate: newTrip.startDate.toISOString(),
               endDate: newTrip.endDate.toISOString(),
               isPublic: false,
               primaryDestination: newTrip.primaryDestination,
               userId: currentUser.uid,
            }),
         });

         const result = await response.json();

         if (!response.ok) {
            throw new Error(result.error || 'Failed to create trip');
         }

         const docRef = { id: result.tripId };

         // Add the new trip to the calendar events
         const newCalendarEvent: CalendarEvent = {
            id: docRef.id,
            title: newTrip.title,
            start: newTrip.startDate,
            end: newTrip.endDate,
            allDay: true,
            type: 'trip',
            isOwner: true,
            resource: {
               ...tripData,
               id: docRef.id,
            },
         };

         setCalendarEvents((prev) => [...prev, newCalendarEvent]);

         // Reset form and close dialog
         setNewTrip({
            title: '',
            startDate: new Date(),
            endDate: new Date(new Date().setDate(new Date().getDate() + 7)),
            primaryDestination: undefined,
            description: '',
         });
         setAddTripDialogOpen(false);
      } catch (error) {
         console.error('Error creating trip:', error);
         setTripSaveError('Failed to create trip. Please try again.');
      } finally {
         setSavingTrip(false);
      }
   };

   // Calendar event styling
   const eventStyleGetter = (event: any) => {
      const isTrip = event.type === 'trip';
      const isOwner = event.isOwner;

      // Base class based on event type
      let className = isTrip ? 'trip-event' : 'calendar-event';

      // Add invited class if not the owner
      if (!isOwner) {
         className += ' invited';
      }

      // Style object (colors are now handled by CSS classes)
      const style: React.CSSProperties = {
         borderRadius: '3px',
         opacity: 0.95,
         color: '#fff',
         border: '0',
         display: 'block',
         padding: '2px 4px',
         fontSize: '10px',
         overflow: 'hidden',
         textOverflow: 'ellipsis',
         whiteSpace: 'nowrap',
         fontWeight: '500',
         lineHeight: '1.2',
         minHeight: '16px',
      };

      return {
         style,
         className,
      };
   };

   // Custom event component optimized for month view
   const EventComponent = ({ event }: { event: CalendarEvent }) => {
      const isTrip = event.type === 'trip';
      const startTime = !event.allDay ? safeFormat(event.start, 'h:mm a') : '';
      const location = isTrip
         ? event.resource?.primaryDestination?.name
         : event.resource?.location?.name;

      // Create a compact display for month view
      let displayText = event.title;

      // Add time for non-all-day events (shortened format)
      if (!event.allDay && startTime) {
         const shortTime = startTime.replace(':00', '').replace(' AM', 'a').replace(' PM', 'p');
         displayText += ` ${shortTime}`;
      }

      // Add location indicator for trips/events with location
      if (location) {
         displayText += ` 📍`;
      }

      const fullTooltip = `${event.title}${startTime ? ` - ${startTime}` : ''}${location ? ` at ${location}` : ''}`;

      return (
         <div className='rbc-event-content' title={fullTooltip}>
            {displayText}
         </div>
      );
   };

   // Add a new interface for multi-year view
   interface YearRangeSlider {
      start: number;
      end: number;
      onChange: (start: number, end: number) => void;
   }

   // Add year range slider component
   const YearRangeSelector: React.FC<YearRangeSlider> = ({ start, end, onChange }) => {
      const [localStart, setLocalStart] = useState(start);
      const [localEnd, setLocalEnd] = useState(end);

      const handleStartChange = (event: SelectChangeEvent<number>) => {
         const newStart = Number(event.target.value);
         setLocalStart(newStart);

         // Ensure end is always >= start
         if (newStart > localEnd) {
            setLocalEnd(newStart);
            onChange(newStart, newStart);
         } else {
            onChange(newStart, localEnd);
         }
      };

      const handleEndChange = (event: SelectChangeEvent<number>) => {
         const newEnd = Number(event.target.value);
         setLocalEnd(newEnd);

         // Ensure start is always <= end
         if (newEnd < localStart) {
            setLocalStart(newEnd);
            onChange(newEnd, newEnd);
         } else {
            onChange(localStart, newEnd);
         }
      };

      // Generate a range of years from current year -10 to current year +10
      const currentYear = getYear(new Date());
      const yearOptions = Array.from({ length: 21 }, (_, i) => currentYear - 10 + i);

      return (
         <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Typography variant='body2'>Year Range:</Typography>
            <FormControl size='small' sx={{ width: 100 }}>
               <Select value={localStart} onChange={handleStartChange}>
                  {yearOptions.map((year) => (
                     <MenuItem key={`start-${year}`} value={year}>
                        {year}
                     </MenuItem>
                  ))}
               </Select>
            </FormControl>
            <Typography variant='body2'>to</Typography>
            <FormControl size='small' sx={{ width: 100 }}>
               <Select value={localEnd} onChange={handleEndChange}>
                  {yearOptions.map((year) => (
                     <MenuItem key={`end-${year}`} value={year}>
                        {year}
                     </MenuItem>
                  ))}
               </Select>
            </FormControl>
         </Box>
      );
   };

   // Update the YearView component to show multiple years
   const MultiYearView: React.FC<{
      startYear: number;
      endYear: number;
      events: CalendarEvent[];
      onSelectSlot: (date: Date) => void;
   }> = ({ startYear, endYear, events, onSelectSlot }) => {
      // Current date info for highlighting current month
      const today = new Date();
      const currentYear = getYear(today);
      const currentMonth = getMonth(today);

      // Generate arrays of years to display
      const years = Array.from({ length: endYear - startYear + 1 }, (_, i) => startYear + i);

      return (
         <Box
            sx={{
               p: isMobile ? 1 : 2,
               overflowY: 'auto',
               maxHeight: isMobile ? 'calc(100vh - 400px)' : 'calc(100vh - 300px)',
            }}
         >
            {years.map((year) => (
               <Box key={year} sx={{ mb: isMobile ? 2 : 4 }}>
                  <Typography
                     variant='h4'
                     gutterBottom
                     sx={{
                        display: 'flex',
                        alignItems: 'center',
                        color: year === currentYear ? 'primary.main' : 'text.primary',
                        fontWeight: year === currentYear ? 'bold' : 'medium',
                        fontSize: isMobile ? fontSize.h4 : fontSize.h3,
                        textAlign: isMobile ? 'center' : 'left',
                        justifyContent: isMobile ? 'center' : 'flex-start',
                     }}
                  >
                     {year === currentYear && <TodayIcon sx={{ mr: 1 }} />}
                     {year}
                  </Typography>

                  <Grid container spacing={isMobile ? 1 : 2}>
                     {Array.from({ length: 12 }, (_, i) => {
                        const monthDate = new Date(year, i, 1);
                        const monthStart = startOfMonth(monthDate);
                        const monthEnd = endOfMonth(monthDate);

                        // Get events for this month
                        const monthEvents = events.filter((event) => {
                           const eventStart = new Date(event.start);
                           const eventEnd = new Date(event.end);
                           return (
                              (eventStart >= monthStart && eventStart <= monthEnd) ||
                              (eventEnd >= monthStart && eventEnd <= monthEnd) ||
                              (eventStart <= monthStart && eventEnd >= monthEnd)
                           );
                        });

                        // Group events by type for counting
                        const tripEvents = monthEvents.filter((e) => e.type === 'trip');
                        const normalEvents = monthEvents.filter((e) => e.type === 'event');

                        const isCurrent = year === currentYear && i === currentMonth;

                        return (
                           <Grid item xs={12} sm={6} md={4} lg={3} key={`${year}-${i}`}>
                              <Paper
                                 elevation={isCurrent ? 6 : 1}
                                 sx={{
                                    p: isMobile ? 1.5 : 2,
                                    cursor: 'pointer',
                                    border: isCurrent ? '2px solid' : 'none',
                                    borderColor: 'primary.main',
                                    bgcolor: isCurrent ? 'rgba(0, 196, 167, 0.10)' : '#0E1118', // bgSurface from theme
                                    transition: 'transform 0.2s, box-shadow 0.2s',
                                    borderRadius: isMobile ? 1 : 2,
                                    '&:hover': {
                                       transform: 'translateY(-4px)',
                                       boxShadow: 4,
                                    },
                                 }}
                                 onClick={() => onSelectSlot(monthDate)}
                              >
                                 <Typography
                                    variant='h6'
                                    fontWeight={isCurrent ? 'bold' : 'medium'}
                                    color={isCurrent ? 'primary' : 'textPrimary'}
                                    gutterBottom
                                    sx={{
                                       fontSize: isMobile ? fontSize.body1 : fontSize.h4,
                                       textAlign: isMobile ? 'center' : 'left',
                                    }}
                                 >
                                    {safeFormat(monthDate, 'MMMM')}
                                 </Typography>

                                 <Box
                                    sx={{
                                       display: 'flex',
                                       gap: isMobile ? 0.5 : 1,
                                       mt: 1,
                                       flexDirection: isMobile ? 'column' : 'row',
                                       alignItems: isMobile ? 'center' : 'flex-start',
                                    }}
                                 >
                                    {tripEvents.length > 0 && (
                                       <Chip
                                          icon={<FlightIcon />}
                                          label={`${tripEvents.length} trip${tripEvents.length !== 1 ? 's' : ''}`}
                                          size={isMobile ? 'small' : 'small'}
                                          color='primary'
                                          variant='outlined'
                                          sx={{
                                             fontSize: isMobile ? '0.75rem' : '0.875rem',
                                             width: isMobile ? '100%' : 'auto',
                                          }}
                                       />
                                    )}
                                    {normalEvents.length > 0 && (
                                       <Chip
                                          icon={<EventIcon />}
                                          label={`${normalEvents.length} event${normalEvents.length !== 1 ? 's' : ''}`}
                                          size={isMobile ? 'small' : 'small'}
                                          color='secondary'
                                          variant='outlined'
                                          sx={{
                                             fontSize: isMobile ? '0.75rem' : '0.875rem',
                                             width: isMobile ? '100%' : 'auto',
                                          }}
                                       />
                                    )}
                                 </Box>

                                 {/* Display first few events if any */}
                                 {monthEvents.length > 0 && (
                                    <Box sx={{ mt: isMobile ? 1 : 2 }}>
                                       {monthEvents.slice(0, isMobile ? 1 : 2).map((event, idx) => (
                                          <Box
                                             key={idx}
                                             sx={{
                                                p: isMobile ? 0.25 : 0.5,
                                                my: isMobile ? 0.25 : 0.5,
                                                borderRadius: isMobile ? 0.5 : 1,
                                                fontSize: isMobile ? '0.7rem' : '0.75rem',
                                                whiteSpace: 'nowrap',
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                                bgcolor:
                                                   event.type === 'trip'
                                                      ? 'rgba(33, 150, 243, 0.1)'
                                                      : 'rgba(156, 39, 176, 0.1)',
                                                color:
                                                   event.type === 'trip'
                                                      ? 'primary.main'
                                                      : 'secondary.main',
                                                textAlign: isMobile ? 'center' : 'left',
                                             }}
                                          >
                                             {safeFormat(new Date(event.start), 'd')}: {event.title}
                                          </Box>
                                       ))}
                                       {monthEvents.length > (isMobile ? 1 : 2) && (
                                          <Typography
                                             variant='caption'
                                             color='text.secondary'
                                             sx={{
                                                fontSize: isMobile ? '0.7rem' : '0.75rem',
                                                textAlign: isMobile ? 'center' : 'left',
                                             }}
                                          >
                                             + {monthEvents.length - (isMobile ? 1 : 2)} more
                                          </Typography>
                                       )}
                                    </Box>
                                 )}
                              </Paper>
                           </Grid>
                        );
                     })}
                  </Grid>
               </Box>
            ))}
         </Box>
      );
   };

   // Update CalendarToolbar component to include multi-year view
   const CalendarToolbar = (props: ToolbarProps) => {
      const goToBack = () => {
         if (calendarView === 'year') {
            // In year view, go back a year
            setCurrentDate(addYears(currentDate, -1));
         } else {
            props.onNavigate('PREV');
         }
      };

      const goToNext = () => {
         if (calendarView === 'year') {
            // In year view, go forward a year
            setCurrentDate(addYears(currentDate, 1));
         } else {
            props.onNavigate('NEXT');
         }
      };

      const goToToday = () => {
         if (calendarView === 'year') {
            // In year view, just update the current date to today
            setCurrentDate(new Date());
         } else {
            props.onNavigate('TODAY');
         }
      };

      return (
         <Box
            sx={{
               display: 'flex',
               flexDirection: { xs: 'column', sm: 'row' },
               justifyContent: 'space-between',
               alignItems: { xs: 'flex-start', sm: 'center' },
               mb: isMobile ? 1 : 2,
               gap: isMobile ? 2 : 1,
            }}
         >
            <Box
               sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  flexDirection: isMobile ? 'column' : 'row',
                  width: isMobile ? '100%' : 'auto',
               }}
            >
               <Typography
                  variant='h5'
                  sx={{
                     mr: isMobile ? 0 : 2,
                     fontSize: isMobile ? fontSize.h4 : fontSize.h3,
                     textAlign: isMobile ? 'center' : 'left',
                  }}
               >
                  {calendarView === 'year' ? `Calendar View` : props.label}
               </Typography>
               <Box
                  sx={{
                     display: 'flex',
                     alignItems: 'center',
                     gap: isMobile ? 1 : 0,
                     flexDirection: isMobile ? 'row' : 'row',
                  }}
               >
                  <IconButton
                     onClick={goToBack}
                     aria-label='Previous'
                     size={isMobile ? 'large' : 'medium'}
                     sx={{
                        minHeight: isMobile ? '48px' : '40px',
                        minWidth: isMobile ? '48px' : '40px',
                     }}
                  >
                     <ChevronLeftIcon />
                  </IconButton>
                  <Button
                     variant='outlined'
                     size={isMobile ? 'large' : 'small'}
                     onClick={goToToday}
                     startIcon={<TodayIcon />}
                     sx={{
                        minHeight: isMobile ? '48px' : '40px',
                        px: isMobile ? 3 : 2,
                        fontSize: isMobile ? fontSize.body1 : 'inherit',
                     }}
                  >
                     Today
                  </Button>
                  <IconButton
                     onClick={goToNext}
                     aria-label='Next'
                     size={isMobile ? 'large' : 'medium'}
                     sx={{
                        minHeight: isMobile ? '48px' : '40px',
                        minWidth: isMobile ? '48px' : '40px',
                     }}
                  >
                     <ChevronRightIcon />
                  </IconButton>
               </Box>
            </Box>

            {calendarView === 'year' && (
               <YearRangeSelector
                  start={yearRange.start}
                  end={yearRange.end}
                  onChange={(start, end) => setYearRange({ start, end })}
               />
            )}

            <Box
               sx={{
                  display: 'flex',
                  alignItems: 'center',
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: isMobile ? 1 : 1,
                  flexWrap: isMobile ? 'wrap' : 'nowrap',
                  justifyContent: isMobile ? 'center' : 'flex-start',
                  width: isMobile ? '100%' : 'auto',
               }}
            >
               <Tooltip title='Year View'>
                  <IconButton
                     onClick={() => handleViewChange('year')}
                     color={calendarView === 'year' ? 'primary' : 'default'}
                     aria-label='Year view'
                     size={isMobile ? 'large' : 'medium'}
                     sx={{
                        minHeight: isMobile ? '48px' : '40px',
                        minWidth: isMobile ? '48px' : '40px',
                     }}
                  >
                     <YearViewIcon />
                  </IconButton>
               </Tooltip>
               <Tooltip title='Month View'>
                  <IconButton
                     onClick={() => props.onView('month')}
                     color={
                        props.view === 'month' && calendarView !== 'year' ? 'primary' : 'default'
                     }
                     aria-label='Month view'
                     size={isMobile ? 'large' : 'medium'}
                     sx={{
                        minHeight: isMobile ? '48px' : '40px',
                        minWidth: isMobile ? '48px' : '40px',
                     }}
                  >
                     <MonthViewIcon />
                  </IconButton>
               </Tooltip>
               <Tooltip title='Week View'>
                  <IconButton
                     onClick={() => props.onView('week')}
                     color={props.view === 'week' ? 'primary' : 'default'}
                     aria-label='Week view'
                     size={isMobile ? 'large' : 'medium'}
                     sx={{
                        minHeight: isMobile ? '48px' : '40px',
                        minWidth: isMobile ? '48px' : '40px',
                     }}
                  >
                     <WeekViewIcon />
                  </IconButton>
               </Tooltip>
               <Tooltip title='Day View'>
                  <IconButton
                     onClick={() => props.onView('day')}
                     color={props.view === 'day' ? 'primary' : 'default'}
                     aria-label='Day view'
                     size={isMobile ? 'large' : 'medium'}
                     sx={{
                        minHeight: isMobile ? '48px' : '40px',
                        minWidth: isMobile ? '48px' : '40px',
                     }}
                  >
                     <DayViewIcon />
                  </IconButton>
               </Tooltip>
            </Box>
         </Box>
      );
   };

   // Add a month navigation function for when clicking on a month in year view
   const handleMonthSelect = (date: Date) => {
      setCurrentDate(date);
      setCalendarView('month');
   };

   // Handle selecting all items for sync
   const handleSelectAllCalendarItems = (select: boolean) => {
      if (select) {
         setSyncItems(filteredEvents.map((event) => event.id));
      } else {
         setSyncItems([]);
      }
   };

   // Handle toggling individual items for sync
   const handleSyncItemToggle = (id: string, checked: boolean) => {
      if (checked) {
         setSyncItems((prev) => [...prev, id]);
      } else {
         setSyncItems((prev) => prev.filter((itemId) => itemId !== id));
      }
   };

   // Handle syncing items to Google Calendar
   const handleSyncToGoogleCalendar = async () => {
      if (!currentUser || syncItems.length === 0) return;

      setSyncingCalendar(true);

      try {
         // Find selected events
         const selectedEvents = filteredEvents.filter((event) => syncItems.includes(event.id));

         // Save sync settings to user profile if auto-sync is enabled
         if (syncSettings.autoSync) {
            // This would typically update a user preferences document in Firestore
         }

         // For batch export, we'll open a single window with all events
         // For small numbers of events, we can open multiple tabs
         if (selectedEvents.length <= 5) {
            // Open each event in a new tab
            selectedEvents.forEach((event) => {
               setTimeout(() => {
                  // Format dates for Google Calendar URL
                  const startDate = encodeURIComponent(
                     event.start.toISOString().replace(/-|:|\.\d+/g, '')
                  );
                  const endDate = encodeURIComponent(
                     event.end.toISOString().replace(/-|:|\.\d+/g, '')
                  );

                  // Build event details
                  const title = encodeURIComponent(event.title);
                  const location =
                     event.type === 'trip'
                        ? encodeURIComponent(event.resource?.primaryDestination?.name || '')
                        : encodeURIComponent(event.resource?.location?.name || '');
                  const description = encodeURIComponent(
                     `${event.resource?.description || ''}\n\nCreated from ItineraryMap App`
                  );

                  // Build Google Calendar URL
                  const googleCalendarUrl = `https://calendar.google.com/calendar/render?action=TEMPLATE&text=${title}&dates=${startDate}/${endDate}&details=${description}&location=${location}`;

                  // Open in a new tab
                  window.open(googleCalendarUrl, '_blank');
               }, 300); // Small delay between opening tabs to prevent popup blocking
            });
         } else {
            // For many events, show toast and open tabs in batches
            setToastMessage(
               `Opening ${selectedEvents.length} items in Google Calendar. Please allow popups.`
            );
            setToastSeverity('success');
            setToastOpen(true);

            // Open events in batches
            let i = 0;
            const openNextBatch = () => {
               const batch = selectedEvents.slice(i, i + 3);
               batch.forEach((event, index) => {
                  setTimeout(() => {
                     // Format dates for Google Calendar URL
                     const startDate = encodeURIComponent(
                        event.start.toISOString().replace(/-|:|\.\d+/g, '')
                     );
                     const endDate = encodeURIComponent(
                        event.end.toISOString().replace(/-|:|\.\d+/g, '')
                     );

                     // Build event details
                     const title = encodeURIComponent(event.title);
                     const location =
                        event.type === 'trip'
                           ? encodeURIComponent(event.resource?.primaryDestination?.name || '')
                           : encodeURIComponent(event.resource?.location?.name || '');
                     const description = encodeURIComponent(
                        `${event.resource?.description || ''}\n\nCreated from ItineraryMap App`
                     );

                     // Build Google Calendar URL
                     const googleCalendarUrl = `https://calendar.google.com/calendar/render?action=TEMPLATE&text=${title}&dates=${startDate}/${endDate}&details=${description}&location=${location}`;

                     // Open in a new tab
                     window.open(googleCalendarUrl, '_blank');
                  }, index * 500); // Stagger opening within batch
               });

               i += 3;
               if (i < selectedEvents.length) {
                  // Wait before opening next batch
                  setTimeout(openNextBatch, 2000);
               }
            };

            openNextBatch();
         }

         // Reset sync items and close dialog
         setTimeout(() => {
            setSyncItems([]);
            setCalendarSyncDialogOpen(false);
            setSyncingCalendar(false);
         }, 1000);
      } catch (error) {
         console.error('Error syncing to Google Calendar:', error);
         setToastMessage('Failed to sync to Google Calendar. Please try again.');
         setToastSeverity('error');
         setToastOpen(true);
         setSyncingCalendar(false);
      }
   };

   return (
      <Container
         maxWidth='xl'
         sx={{
            mt: isMobile ? 2 : 5,
            px: isMobile ? 1 : 2,
         }}
      >
         <Box
            sx={{
               mb: isMobile ? 2 : 3,
               display: 'flex',
               justifyContent: 'space-between',
               alignItems: isMobile ? 'flex-start' : 'center',
               flexDirection: isMobile ? 'column' : 'row',
               gap: isMobile ? 2 : 0,
            }}
         >
            <Box>
               <Typography
                  variant='h4'
                  component='h1'
                  sx={{
                     display: 'flex',
                     alignItems: 'center',
                     gap: 1,
                     fontSize: isMobile ? fontSize.h4 : fontSize.h3,
                     textAlign: isMobile ? 'center' : 'left',
                  }}
               >
                  <CalendarIcon color='primary' /> Travel Calendar
               </Typography>
               <Typography variant='body2' color='text.secondary' sx={{ mt: 0.5 }}>
                  Your events and trips in one place.
               </Typography>
            </Box>

            <Box
               sx={{
                  display: 'flex',
                  gap: isMobile ? 1 : 1,
                  flexDirection: isMobile ? 'column' : 'row',
                  width: isMobile ? '100%' : 'auto',
                  flexWrap: 'wrap',
               }}
            >
               <Button
                  variant='contained'
                  startIcon={<EventIcon />}
                  onClick={() => {
                     if (checkSubscriptionStatus()) {
                        setAddEventDialogOpen(true);
                     }
                  }}
                  size={isMobile ? 'large' : 'medium'}
                  sx={{
                     fontWeight: 600,
                     borderRadius: 2,
                     minHeight: isMobile ? '48px' : '40px',
                     px: isMobile ? 3 : 2,
                     fontSize: isMobile ? fontSize.body1 : 'inherit',
                     width: isMobile ? '100%' : 'auto',
                     background: '#00C4A7', // teal from theme
                     boxShadow: '0 4px 15px rgba(0, 196, 167, 0.3)',
                     '&:hover': {
                        background: '#00DFC0', // teal light from theme
                        boxShadow: '0 6px 20px rgba(0, 196, 167, 0.4)',
                     },
                  }}
               >
                  Add Event
               </Button>
               <Button
                  variant='outlined'
                  onClick={() => setFilterDialogOpen(true)}
                  startIcon={<FilterIcon />}
                  size={isMobile ? 'large' : 'medium'}
                  sx={{
                     fontWeight: 600,
                     borderRadius: 2,
                     minHeight: isMobile ? '48px' : '40px',
                     px: isMobile ? 3 : 2,
                     fontSize: isMobile ? fontSize.body1 : 'inherit',
                     width: isMobile ? '100%' : 'auto',
                  }}
               >
                  Filter Calendar
               </Button>
               <Button
                  variant='outlined'
                  size={isMobile ? 'large' : 'small'}
                  onClick={() => {
                     if (checkSubscriptionStatus()) {
                        setCalendarSyncDialogOpen(true);
                     }
                  }}
                  sx={{
                     fontWeight: 600,
                     borderRadius: 2,
                     minHeight: isMobile ? '48px' : '40px',
                     px: isMobile ? 3 : 2,
                     fontSize: isMobile ? fontSize.body1 : 'inherit',
                     width: isMobile ? '100%' : 'auto',
                  }}
               >
                  Sync to Google
               </Button>
            </Box>
         </Box>

         <Paper
            elevation={3}
            sx={{
               p: isMobile ? 1 : 2,
               // height: isMobile ? 'calc(100vh - 300px)' : 'calc(100vh - 230px)',
               minHeight: isMobile ? '400px' : '450px',
               borderRadius: isMobile ? 1 : 2,
            }}
         >
            {loading ? (
               <Box
                  sx={{
                     display: 'flex',
                     justifyContent: 'center',
                     alignItems: 'center',
                     //height: '100%',
                  }}
               >
                  <CircularProgress />
               </Box>
            ) : error ? (
               <Alert severity='error'>{error}</Alert>
            ) : (
               <>
                  {filteredEvents.length === 0 && (
                     <Box
                        sx={{
                           textAlign: 'center',
                           py: 3,
                           px: 2,
                           borderBottom: '1px solid',
                           borderColor: 'divider',
                           mb: 2,
                        }}
                     >
                        <Typography variant='body1' color='text.secondary' sx={{ mb: 2 }}>
                           No events in this view. Add an event or adjust your filters.
                        </Typography>
                        <Button
                           variant='contained'
                           startIcon={<EventIcon />}
                           onClick={() => {
                              if (checkSubscriptionStatus()) {
                                 setAddEventDialogOpen(true);
                              }
                           }}
                           sx={{
                              fontWeight: 600,
                              borderRadius: 2,
                              background: 'linear-gradient(135deg, #01949A 0%, #004369 100%)',
                              boxShadow: '0 4px 15px rgba(1, 148, 154, 0.3)',
                              '&:hover': {
                                 background: '#00DFC0', // teal light from theme
                                 boxShadow: '0 6px 20px rgba(0, 196, 167, 0.4)',
                              },
                           }}
                        >
                           Add Event
                        </Button>
                     </Box>
                  )}
                  <div style={{ height: '100%' }}>
                  {calendarView === 'year' ? (
                     <MultiYearView
                        startYear={yearRange.start}
                        endYear={yearRange.end}
                        events={filteredEvents}
                        onSelectSlot={handleMonthSelect}
                     />
                  ) : (
                     <ReactBigCalendar.Calendar
                        localizer={localizer}
                        events={filteredEvents}
                        startAccessor='start'
                        endAccessor='end'
                        style={{ height: '100%', fontSize: '12px' }}
                        view={calendarView as ReactBigCalendar.View}
                        onView={handleViewChange as any}
                        date={currentDate}
                        onNavigate={handleNavigate}
                        onSelectEvent={handleSelectEvent as any}
                        eventPropGetter={eventStyleGetter as any}
                        showAllEvents={true}
                        popup={true}
                        popupOffset={20}
                        components={{
                           toolbar: CalendarToolbar,
                           event: EventComponent as any,
                        }}
                        views={{
                           month: true,
                           week: true,
                           day: true,
                           agenda: true,
                        }}
                        step={30}
                        timeslots={2}
                     />
                  )}
                  </div>
               </>
            )}
         </Paper>

         {/* Filter Dialog */}
         <Dialog open={filterDialogOpen} onClose={() => setFilterDialogOpen(false)}>
            <DialogTitle>
               <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Typography variant='h6'>
                     <FilterIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                     Filter Calendar
                  </Typography>
                  <IconButton onClick={() => setFilterDialogOpen(false)}>
                     <CloseIcon />
                  </IconButton>
               </Box>
            </DialogTitle>
            <DialogContent>
               <Typography variant='body2' color='text.secondary' sx={{ mb: 2 }}>
                  Trips are hidden on the calendar. Only events are shown; use the options below to
                  filter by ownership.
               </Typography>
               <FormControl component='fieldset' sx={{ mt: 1 }}>
                  <Typography variant='subtitle2' gutterBottom>
                     Events
                  </Typography>
                  <FormGroup>
                     <FormControlLabel
                        control={
                           <Checkbox
                              checked={filterOptions.showEvents}
                              onChange={handleFilterChange}
                              name='showEvents'
                              color='secondary'
                           />
                        }
                        label='Show events'
                     />
                  </FormGroup>
               </FormControl>

               <Divider sx={{ my: 2 }} />

               <FormControl component='fieldset'>
                  <Typography variant='subtitle2' gutterBottom>
                     Ownership
                  </Typography>
                  <FormGroup>
                     <FormControlLabel
                        control={
                           <Checkbox
                              checked={filterOptions.showOwned}
                              onChange={handleFilterChange}
                              name='showOwned'
                           />
                        }
                        label='Created by you'
                     />
                     <FormControlLabel
                        control={
                           <Checkbox
                              checked={filterOptions.showInvited}
                              onChange={handleFilterChange}
                              name='showInvited'
                           />
                        }
                        label='Invited to'
                     />
                  </FormGroup>
               </FormControl>
            </DialogContent>
            <DialogActions>
               <Button
                  onClick={() => {
                     setFilterOptions({
                        showTrips: true,
                        showEvents: true,
                        showOwned: true,
                        showInvited: true,
                     });
                  }}
               >
                  Reset
               </Button>
               <Button onClick={() => setFilterDialogOpen(false)} variant='contained'>
                  Apply
               </Button>
            </DialogActions>
         </Dialog>

         {/* New Trip/Event Modal */}
         <TripEventModal
            open={modalOpen}
            onClose={() => {
               setModalOpen(false);
               setModalData(null);
            }}
            data={modalData}
         />

         {/* Add Event Dialog */}
         <Dialog
            open={addEventDialogOpen}
            onClose={() => setAddEventDialogOpen(false)}
            maxWidth='sm'
            fullWidth
         >
            <DialogTitle>
               <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Typography variant='h6'>
                     <EventIcon color='secondary' sx={{ mr: 1, verticalAlign: 'middle' }} />
                     Add New Event
                  </Typography>
                  <IconButton onClick={() => setAddEventDialogOpen(false)} size='small'>
                     <CloseIcon />
                  </IconButton>
               </Box>
            </DialogTitle>
            <DialogContent dividers>
               {eventSaveError && (
                  <Alert severity='error' sx={{ mb: 2 }}>
                     {eventSaveError}
                  </Alert>
               )}
               <Grid container spacing={2}>
                  <Grid item xs={12}>
                     <TextField
                        label='Event Title'
                        fullWidth
                        required
                        value={newEvent.title}
                        onChange={(e) =>
                           setNewEvent((prev) => ({ ...prev, title: e.target.value }))
                        }
                        disabled={savingEvent}
                     />
                  </Grid>

                  <Grid item xs={12}>
                     <FormControl fullWidth sx={{ mb: 2 }}>
                        <InputLabel id='event-type-label'>Event Type</InputLabel>
                        <Select
                           labelId='event-type-label'
                           value={newEvent.eventType}
                           label='Event Type'
                           onChange={(e) =>
                              setNewEvent((prev) => ({ ...prev, eventType: e.target.value }))
                           }
                           disabled={savingEvent}
                        >
                           <MenuItem value='personal'>Personal</MenuItem>
                           <MenuItem value='business'>Business</MenuItem>
                           <MenuItem value='vacation'>Vacation Activity</MenuItem>
                           <MenuItem value='reminder'>Reminder</MenuItem>
                           <MenuItem value='social'>Social</MenuItem>
                           <MenuItem value='other'>Other</MenuItem>
                        </Select>
                     </FormControl>
                  </Grid>

                  <LocalizationProvider dateAdapter={AdapterDateFns}>
                     <Grid item xs={12} sm={6}>
                        <Box sx={{ width: '100%' }}>
                           <DateTimePicker<Date>
                              label='Start Date & Time'
                              value={newEvent.startDate}
                              onChange={(newValue: Date | null) => {
                                 if (newValue) {
                                    setNewEvent((prev) => {
                                       // If the start date is after the end date, update the end date too
                                       const updatedEndDate =
                                          newValue > prev.endDate
                                             ? new Date(newValue.getTime() + 2 * 60 * 60 * 1000)
                                             : prev.endDate;
                                       return {
                                          ...prev,
                                          startDate: newValue,
                                          endDate: updatedEndDate,
                                       };
                                    });
                                 }
                              }}
                              disabled={savingEvent}
                              renderInput={(props) => <TextField {...props} fullWidth />}
                           />
                        </Box>
                     </Grid>
                     <Grid item xs={12} sm={6}>
                        <Box sx={{ width: '100%' }}>
                           <DateTimePicker<Date>
                              label='End Date & Time'
                              value={newEvent.endDate}
                              onChange={(newValue: Date | null) => {
                                 if (newValue) {
                                    setNewEvent((prev) => ({ ...prev, endDate: newValue }));
                                 }
                              }}
                              disabled={savingEvent}
                              minDate={newEvent.startDate}
                              renderInput={(props) => <TextField {...props} fullWidth />}
                           />
                        </Box>
                     </Grid>
                  </LocalizationProvider>
                  {/* 
                  <Grid item xs={12}>
                     <TextField
                        label='Location Name'
                        fullWidth
                        value={newEvent.location?.name || ''}
                        onChange={(e) =>
                           setNewEvent((prev) => ({
                              ...prev,
                              location: { ...prev.location, name: e.target.value } as any,
                           }))
                        }
                        disabled={savingEvent}
                        placeholder='e.g., Central Park'
                     />
                  </Grid> */}

                  <Grid item xs={12}>
                     {/* <TextField
                        label='Address'
                        fullWidth
                        value={newEvent.location?.address || ''}
                        onChange={(e) =>
                           setNewEvent((prev) => ({
                              ...prev,
                              location: { ...prev.location, address: e.target.value } as any,
                           }))
                        }
                        disabled={savingEvent}
                        placeholder='e.g., New York, NY'
                     /> */}
                     <GooglePlacesSearch
                        //  sx={{ mb: 2 }}
                        value={newEvent.location?.address || ''}
                        searchType='places'
                        label='Location'
                        fullWidth
                        onPlaceSelect={(place_name: any, lat: any, lng: any) => {
                           setNewEvent((prev) => ({
                              ...prev,
                              location: {
                                 address: place_name,
                                 name: place_name,
                                 coordinates: [lat, lng],
                              },
                           }));
                        }}
                     />
                  </Grid>

                  <Grid item xs={12}>
                     <TextField
                        label='Description'
                        fullWidth
                        multiline
                        rows={3}
                        value={newEvent.description || ''}
                        onChange={(e) =>
                           setNewEvent((prev) => ({ ...prev, description: e.target.value }))
                        }
                        disabled={savingEvent}
                        placeholder='Add details about this event...'
                     />
                  </Grid>
               </Grid>
            </DialogContent>
            <DialogActions>
               <Button onClick={() => setAddEventDialogOpen(false)} disabled={savingEvent}>
                  Cancel
               </Button>
               <Button
                  variant='contained'
                  color='secondary'
                  onClick={createEvent}
                  disabled={savingEvent}
                  startIcon={savingEvent ? <CircularProgress size={20} /> : <EventIcon />}
               >
                  {savingEvent ? 'Saving...' : 'Create Event'}
               </Button>
            </DialogActions>
         </Dialog>

         {/* Add Trip Dialog */}
         <Dialog
            open={addTripDialogOpen}
            onClose={() => setAddTripDialogOpen(false)}
            maxWidth='sm'
            fullWidth
         >
            <DialogTitle>
               <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Typography variant='h6'>
                     <FlightIcon color='primary' sx={{ mr: 1, verticalAlign: 'middle' }} />
                     Add New Trip
                  </Typography>
                  <IconButton onClick={() => setAddTripDialogOpen(false)} size='small'>
                     <CloseIcon />
                  </IconButton>
               </Box>
            </DialogTitle>
            <DialogContent dividers>
               {tripSaveError && (
                  <Alert severity='error' sx={{ mb: 2 }}>
                     {tripSaveError}
                  </Alert>
               )}
               <Grid container spacing={2}>
                  <Grid item xs={12}>
                     <TextField
                        label='Trip Title'
                        fullWidth
                        required
                        value={newTrip.title}
                        onChange={(e) => setNewTrip((prev) => ({ ...prev, title: e.target.value }))}
                        disabled={savingTrip}
                        placeholder='e.g., Summer Vacation'
                     />
                  </Grid>

                  <LocalizationProvider dateAdapter={AdapterDateFns}>
                     <Grid item xs={12} sm={6}>
                        <Box sx={{ width: '100%' }}>
                           <DateTimePicker<Date>
                              label='Start Date'
                              value={newTrip.startDate}
                              onChange={(newValue: Date | null) => {
                                 if (newValue) {
                                    setNewTrip((prev) => {
                                       // If the start date is after the end date, update the end date too
                                       const updatedEndDate =
                                          newValue > prev.endDate
                                             ? new Date(
                                                  newValue.getTime() + 7 * 24 * 60 * 60 * 1000
                                               )
                                             : prev.endDate;
                                       return {
                                          ...prev,
                                          startDate: newValue,
                                          endDate: updatedEndDate,
                                       };
                                    });
                                 }
                              }}
                              disabled={savingTrip}
                              renderInput={(props) => <TextField {...props} fullWidth />}
                           />
                        </Box>
                     </Grid>
                     <Grid item xs={12} sm={6}>
                        <Box sx={{ width: '100%' }}>
                           <DateTimePicker<Date>
                              label='End Date'
                              value={newTrip.endDate}
                              onChange={(newValue: Date | null) => {
                                 if (newValue) {
                                    setNewTrip((prev) => ({ ...prev, endDate: newValue }));
                                 }
                              }}
                              disabled={savingTrip}
                              minDate={newTrip.startDate}
                              renderInput={(props) => <TextField {...props} fullWidth />}
                           />
                        </Box>
                     </Grid>
                  </LocalizationProvider>

                  <Grid item xs={12}>
                     {/* <TextField
                        label='Destination'
                        fullWidth
                        value={newTrip.primaryDestination?.name || ''}
                        onChange={(e) =>
                           setNewTrip((prev) => ({
                              ...prev,
                              primaryDestination: {
                                 ...prev.primaryDestination,
                                 name: e.target.value,
                                 coordinates: prev.primaryDestination?.coordinates || [0, 0],
                              } as any,
                           }))
                        }
                        disabled={savingTrip}
                        placeholder='e.g., Paris, France'
                     /> */}
                     <GooglePlacesSearch
                        //  sx={{ mb: 2 }}
                        value={newTrip.primaryDestination?.name || ''}
                        searchType='places'
                        label='Destination'
                        fullWidth
                        onPlaceSelect={(place_name: any, lat: any, lng: any) => {
                           setNewTrip((prev) => ({
                              ...prev,
                              primaryDestination: {
                                 address: place_name,
                                 name: place_name,
                                 coordinates: [lat, lng],
                              },
                           }));
                        }}
                     />
                  </Grid>

                  <Grid item xs={12}>
                     <TextField
                        label='Description'
                        fullWidth
                        multiline
                        rows={3}
                        value={newTrip.description || ''}
                        onChange={(e) =>
                           setNewTrip((prev) => ({ ...prev, description: e.target.value }))
                        }
                        disabled={savingTrip}
                        placeholder='Add details about this trip...'
                     />
                  </Grid>
               </Grid>
            </DialogContent>
            <DialogActions>
               <Button onClick={() => setAddTripDialogOpen(false)} disabled={savingTrip}>
                  Cancel
               </Button>
               <Button
                  variant='contained'
                  color='primary'
                  onClick={createTrip}
                  disabled={savingTrip}
                  startIcon={savingTrip ? <CircularProgress size={20} /> : <FlightIcon />}
               >
                  {savingTrip ? 'Saving...' : 'Create Trip'}
               </Button>
            </DialogActions>
         </Dialog>

         {/* Google Calendar Sync Dialog */}
         <Dialog
            open={calendarSyncDialogOpen}
            onClose={() => setCalendarSyncDialogOpen(false)}
            maxWidth='md'
            fullWidth
         >
            <DialogTitle>
               <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Typography variant='h6'>
                     <CalendarIcon color='primary' sx={{ mr: 1, verticalAlign: 'middle' }} />
                     Google Calendar Sync
                  </Typography>
                  <IconButton onClick={() => setCalendarSyncDialogOpen(false)} size='small'>
                     <CloseIcon />
                  </IconButton>
               </Box>
            </DialogTitle>
            <DialogContent dividers>
               <Grid container spacing={2}>
                  <Grid item xs={12}>
                     <Alert severity='info' sx={{ mb: 2 }}>
                        Select events and trips you want to add to your Google Calendar. Items will
                        be exported as a one-time sync. You can set up automatic sync in your
                        profile settings.
                     </Alert>
                  </Grid>

                  <Grid item xs={12}>
                     <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                        <Typography variant='subtitle1'>Select items to sync</Typography>
                        <Box>
                           <Button
                              size='small'
                              onClick={() => handleSelectAllCalendarItems(true)}
                              sx={{ mr: 1 }}
                           >
                              Select All
                           </Button>
                           <Button size='small' onClick={() => handleSelectAllCalendarItems(false)}>
                              Clear
                           </Button>
                        </Box>
                     </Box>

                     <FormControl component='fieldset' sx={{ width: '100%' }}>
                        <Typography variant='subtitle2' gutterBottom>
                           Your Trips
                        </Typography>
                        <Box sx={{ maxHeight: '200px', overflowY: 'auto', mb: 2 }}>
                           {filteredEvents
                              .filter((event) => event.type === 'trip' && event.isOwner)
                              .map((event) => (
                                 <FormControlLabel
                                    key={event.id}
                                    control={
                                       <Checkbox
                                          checked={syncItems.includes(event.id)}
                                          onChange={(e) =>
                                             handleSyncItemToggle(event.id, e.target.checked)
                                          }
                                       />
                                    }
                                    label={
                                       <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                                          <Typography variant='body1'>{event.title}</Typography>
                                          <Typography variant='caption' color='text.secondary'>
                                             {safeFormat(event.start, 'MMM d, yyyy')} -{' '}
                                             {safeFormat(event.end, 'MMM d, yyyy')}
                                          </Typography>
                                       </Box>
                                    }
                                 />
                              ))}
                           {filteredEvents.filter((event) => event.type === 'trip' && event.isOwner)
                              .length === 0 && (
                              <Typography variant='body2' color='text.secondary' sx={{ ml: 2 }}>
                                 No trips found
                              </Typography>
                           )}
                        </Box>

                        <Typography variant='subtitle2' gutterBottom>
                           Invited Trips
                        </Typography>
                        <Box sx={{ maxHeight: '200px', overflowY: 'auto', mb: 2 }}>
                           {filteredEvents
                              .filter((event) => event.type === 'trip' && !event.isOwner)
                              .map((event) => (
                                 <FormControlLabel
                                    key={event.id}
                                    control={
                                       <Checkbox
                                          checked={syncItems.includes(event.id)}
                                          onChange={(e) =>
                                             handleSyncItemToggle(event.id, e.target.checked)
                                          }
                                       />
                                    }
                                    label={
                                       <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                                          <Typography variant='body1'>{event.title}</Typography>
                                          <Typography variant='caption' color='text.secondary'>
                                             {safeFormat(event.start, 'MMM d, yyyy')} -{' '}
                                             {safeFormat(event.end, 'MMM d, yyyy')}
                                          </Typography>
                                       </Box>
                                    }
                                 />
                              ))}
                           {filteredEvents.filter(
                              (event) => event.type === 'trip' && !event.isOwner
                           ).length === 0 && (
                              <Typography variant='body2' color='text.secondary' sx={{ ml: 2 }}>
                                 No invited trips found
                              </Typography>
                           )}
                        </Box>

                        <Typography variant='subtitle2' gutterBottom>
                           Your Events
                        </Typography>
                        <Box sx={{ maxHeight: '200px', overflowY: 'auto', mb: 2 }}>
                           {filteredEvents
                              .filter((event) => event.type === 'event' && event.isOwner)
                              .map((event) => (
                                 <FormControlLabel
                                    key={event.id}
                                    control={
                                       <Checkbox
                                          checked={syncItems.includes(event.id)}
                                          onChange={(e) =>
                                             handleSyncItemToggle(event.id, e.target.checked)
                                          }
                                       />
                                    }
                                    label={
                                       <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                                          <Typography variant='body1'>{event.title}</Typography>
                                          <Typography variant='caption' color='text.secondary'>
                                             {safeFormat(event.start, 'MMM d, yyyy')}
                                             {!event.allDay &&
                                                ` ${safeFormat(event.start, 'h:mm a')}`}
                                          </Typography>
                                       </Box>
                                    }
                                 />
                              ))}
                           {filteredEvents.filter(
                              (event) => event.type === 'event' && event.isOwner
                           ).length === 0 && (
                              <Typography variant='body2' color='text.secondary' sx={{ ml: 2 }}>
                                 No events found
                              </Typography>
                           )}
                        </Box>

                        <Typography variant='subtitle2' gutterBottom>
                           Invited Events
                        </Typography>
                        <Box sx={{ maxHeight: '200px', overflowY: 'auto', mb: 2 }}>
                           {filteredEvents
                              .filter((event) => event.type === 'event' && !event.isOwner)
                              .map((event) => (
                                 <FormControlLabel
                                    key={event.id}
                                    control={
                                       <Checkbox
                                          checked={syncItems.includes(event.id)}
                                          onChange={(e) =>
                                             handleSyncItemToggle(event.id, e.target.checked)
                                          }
                                       />
                                    }
                                    label={
                                       <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                                          <Typography variant='body1'>{event.title}</Typography>
                                          <Typography variant='caption' color='text.secondary'>
                                             {safeFormat(event.start, 'MMM d, yyyy')}
                                             {!event.allDay &&
                                                ` ${safeFormat(event.start, 'h:mm a')}`}
                                          </Typography>
                                       </Box>
                                    }
                                 />
                              ))}
                           {filteredEvents.filter(
                              (event) => event.type === 'event' && !event.isOwner
                           ).length === 0 && (
                              <Typography variant='body2' color='text.secondary' sx={{ ml: 2 }}>
                                 No invited events found
                              </Typography>
                           )}
                        </Box>
                     </FormControl>
                  </Grid>

                  <Grid item xs={12}>
                     <FormControlLabel
                        control={
                           <Checkbox
                              checked={syncSettings.autoSync}
                              onChange={(e) =>
                                 setSyncSettings((prev) => ({
                                    ...prev,
                                    autoSync: e.target.checked,
                                 }))
                              }
                           />
                        }
                        label='Automatically sync new events/trips to Google Calendar'
                     />
                  </Grid>
               </Grid>
            </DialogContent>
            <DialogActions>
               <Button onClick={() => setCalendarSyncDialogOpen(false)}>Cancel</Button>
               <Button
                  variant='outlined'
                  onClick={() => navigate('/profile/calendar-settings')}
                  startIcon={<PersonIcon />}
               >
                  Manage in Profile
               </Button>
               <Button
                  variant='contained'
                  color='primary'
                  onClick={handleSyncToGoogleCalendar}
                  startIcon={syncingCalendar ? <CircularProgress size={20} /> : <CalendarIcon />}
                  disabled={syncingCalendar || syncItems.length === 0}
               >
                  {syncingCalendar ? 'Syncing...' : `Sync ${syncItems.length} Items`}
               </Button>
            </DialogActions>
         </Dialog>

         {/* Toast Notification */}
         <Snackbar
            open={toastOpen}
            autoHideDuration={6000}
            onClose={() => setToastOpen(false)}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
         >
            <Alert
               onClose={() => setToastOpen(false)}
               severity={toastSeverity}
               sx={{ width: '100%' }}
            >
               {toastMessage}
            </Alert>
         </Snackbar>
      </Container>
   );
};

export default CalendarPage; 